import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ReturnRow, ReturnStatus } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Segmented, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hq } from '../api/client';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

type StatusFilter = 'ALL' | ReturnStatus;

const STATUS_META: Record<string, { text: string; color: string }> = {
  NEW: { text: '待提交', color: 'default' },
  SUBMITTED: { text: '已提交', color: 'blue' },
  AUDITED: { text: '已审核', color: 'cyan' },
  PICKING: { text: '取件中', color: 'processing' },
  RECEIVED: { text: '已收回', color: 'blue' },
  CHECKED: { text: '已判定', color: 'cyan' },
  REFUNDED: { text: '已退款', color: 'green' },
  EXCHANGED: { text: '已换货', color: 'green' },
  CLOSED: { text: '已关闭', color: 'default' },
  REJECTED: { text: '不予受理', color: 'red' }
};

const TYPE_TEXT: Record<string, string> = {
  RETURN_REFUND: '退货退款',
  EXCHANGE: '换货',
  DISCOUNT: '折让'
};

const REASON_TEXT: Record<string, string> = {
  QUALITY: '质量问题',
  WRONG_ITEM: '错发/漏发',
  NEAR_EXPIRY: '近效期',
  DAMAGE: '破损',
  OVER_ORDER: '超量配送',
  POLICY: '政策调整'
};

const FILTERS: { label: string; value: StatusFilter }[] = [
  { label: '全部', value: 'ALL' },
  { label: '待提交', value: 'NEW' },
  { label: '取件中', value: 'PICKING' },
  { label: '已收回', value: 'RECEIVED' },
  { label: '已退款', value: 'REFUNDED' },
  { label: '已换货', value: 'EXCHANGED' },
  { label: '已关闭', value: 'CLOSED' }
];

export default function ReturnList() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<StatusFilter>('ALL');

  const query = useInfiniteQuery({
    queryKey: ['return', 'list', status],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.returns.list({
        lastId: pageParam,
        size: PAGE_SIZE,
        returnStatus: status === 'ALL' ? undefined : status
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<ReturnRow> = [
    {
      title: '退货单号',
      dataIndex: 'bizNo',
      width: 230,
      render: (value: string, row) => <Typography.Link onClick={() => navigate(`/return/${row.id}`)}>{value}</Typography.Link>
    },
    { title: '类型', dataIndex: 'returnType', width: 110, render: (value: string) => TYPE_TEXT[value] ?? value },
    { title: '原因', dataIndex: 'reasonType', width: 120, render: (value: string | null) => (value ? REASON_TEXT[value] ?? value : '-') },
    { title: '责任供应商', dataIndex: 'supplierName', ellipsis: { showTitle: false }, render: ellipsisCell('supplierName') },
    {
      title: '状态',
      dataIndex: 'returnStatus',
      width: 110,
      render: (value: string) => {
        const meta = STATUS_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '总数量', dataIndex: 'totalQty', width: 110, align: 'right', render: (value: number) => formatQty(value) },
    {
      title: '退货金额',
      dataIndex: 'returnAmount',
      width: 130,
      align: 'right',
      render: (value: number) => <Typography.Text type="danger">{formatAmount(value)}</Typography.Text>
    },
    { title: '业务时间', dataIndex: 'bizDate', width: 160, render: (value: string | null) => formatDisplayTime(value) },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_value: unknown, row) => (
        <Button type="link" onClick={() => navigate(`/return/${row.id}`)}>
          详情
        </Button>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title="退换货"
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} loading={query.isFetching}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/return/create')}>
            新建退货
          </Button>
        </Space>
      }
    >
      <Segmented<StatusFilter>
        value={status}
        options={FILTERS}
        onChange={(value) => setStatus(value)}
        style={{ marginBottom: 12 }}
      />

      {query.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="退货单加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}

      <Table<ReturnRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: query.isError ? '加载失败，请重试' : '暂无退货单' }}
        onRow={(row) => ({ onDoubleClick: () => navigate(`/return/${row.id}`) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {query.hasNextPage ? (
          <Button onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage}>
            加载更多
          </Button>
        ) : rows.length > 0 ? (
          <Typography.Text type="secondary">没有更多了</Typography.Text>
        ) : null}
      </Space>
    </Card>
  );
}
